import Vue from 'vue'
import { register } from 'register-service-worker'

const INSTALL_SNOOZE_KEY = 'sorority-wars-pwa-install-snoozed-until'
const UPDATE_ATTEMPT_KEY = 'sorority-wars-pwa-update-attempted-at'
const UPDATE_DEFERRED_KEY = 'sorority-wars-pwa-update-deferred'
const INSTALL_SNOOZE_MS = 30 * 24 * 60 * 60 * 1000
const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000
const UPDATE_RECOVERY_WINDOW_MS = 5 * 60 * 1000
const UPDATE_SETTLE_TIMEOUT_MS = 12000

function readTimestamp (key) {
  try {
    return Number(window.localStorage.getItem(key)) || 0
  } catch (error) {
    return 0
  }
}

function writeTimestamp (key, value) {
  try {
    window.localStorage.setItem(key, String(value))
  } catch (error) {
    // Local storage can be unavailable in private browsing or restricted webviews.
  }
}

function readSessionTimestamp (key) {
  try {
    return Number(window.sessionStorage.getItem(key)) || 0
  } catch (error) {
    return 0
  }
}

function readSessionFlag (key) {
  try {
    return window.sessionStorage.getItem(key) === 'true'
  } catch (error) {
    return false
  }
}

function writeSessionValue (key, value) {
  try {
    window.sessionStorage.setItem(key, String(value))
  } catch (error) {
    // Session storage can be unavailable in private browsing or restricted webviews.
  }
}

function removeSessionValue (key) {
  try {
    window.sessionStorage.removeItem(key)
  } catch (error) {
    // Session storage can be unavailable in private browsing or restricted webviews.
  }
}

function hasRecentUpdateAttempt () {
  const attemptedAt = readSessionTimestamp(UPDATE_ATTEMPT_KEY)
  const elapsed = Date.now() - attemptedAt
  return attemptedAt > 0 && elapsed >= 0 && elapsed <= UPDATE_RECOVERY_WINDOW_MS
}

function detectStandalone () {
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true
}

function detectIOS () {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent) ||
    (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1)
}

const state = Vue.observable({
  production: process.env.NODE_ENV === 'production',
  supported: 'serviceWorker' in navigator,
  checkingInstall: process.env.NODE_ENV === 'production' && !detectStandalone(),
  installPrompt: null,
  installable: false,
  installed: detectStandalone(),
  isIOS: detectIOS(),
  registration: null,
  registrationError: '',
  checkingUpdate: false,
  updateAvailable: false,
  updating: false,
  updateError: '',
  updateRecoveryAvailable: false,
  updateDeferred: readSessionFlag(UPDATE_DEFERRED_KEY),
  installDismissedUntil: readTimestamp(INSTALL_SNOOZE_KEY),
  lastUpdateCheck: 0,
  now: Date.now()
})

let initialized = false
let reloading = false

const pwaManager = {
  state,

  init () {
    if (initialized) return
    initialized = true

    window.addEventListener('beforeinstallprompt', event => {
      event.preventDefault()
      state.installPrompt = event
      state.installable = true
      state.checkingInstall = false
      state.now = Date.now()
    })

    window.addEventListener('appinstalled', () => {
      state.installPrompt = null
      state.installable = false
      state.installed = true
      state.now = Date.now()
    })

    window.setTimeout(() => {
      state.checkingInstall = false
    }, 2000)

    const displayMode = window.matchMedia('(display-mode: standalone)')
    const updateDisplayMode = () => {
      state.installed = detectStandalone()
      if (state.installed) {
        state.installPrompt = null
        state.installable = false
      }
    }
    if (displayMode.addEventListener) {
      displayMode.addEventListener('change', updateDisplayMode)
    } else if (displayMode.addListener) {
      displayMode.addListener(updateDisplayMode)
    }

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return
      state.now = Date.now()
      if (state.now - state.lastUpdateCheck >= UPDATE_CHECK_INTERVAL_MS) {
        this.checkForUpdate()
      }
    })

    if (process.env.NODE_ENV === 'production') {
      this.registerServiceWorker()
    }
  },

  registerServiceWorker () {
    register(`${process.env.BASE_URL}service-worker.js`, {
      registrationOptions: {
        updateViaCache: 'none'
      },
      ready: registration => {
        state.registration = registration
      },
      registered: registration => {
        state.registration = registration
        state.registrationError = ''
        this.checkForUpdate()
      },
      updated: registration => {
        if (!state.checkingUpdate) this.setUpdateAvailable(registration)
      },
      error: error => {
        state.checkingUpdate = false
        state.registrationError = error && error.message
          ? error.message
          : 'Service-worker registration failed.'
      }
    })
  },

  setUpdateAvailable (registration) {
    state.registration = registration
    state.updateAvailable = Boolean(registration && registration.waiting)
    state.updateRecoveryAvailable = state.updateAvailable && (Boolean(state.updateError) || hasRecentUpdateAttempt())
    if (!state.updateAvailable) {
      state.updating = false
      state.updateError = ''
      state.updateRecoveryAvailable = false
      state.updateDeferred = false
      removeSessionValue(UPDATE_ATTEMPT_KEY)
      removeSessionValue(UPDATE_DEFERRED_KEY)
    }
    state.now = Date.now()
  },

  waitForInstallingWorker (registration) {
    const worker = registration && registration.installing
    if (!worker || ['installed', 'activated', 'redundant'].includes(worker.state)) {
      return Promise.resolve()
    }

    return new Promise(resolve => {
      let timeout = null
      const finish = () => {
        worker.removeEventListener('statechange', onStateChange)
        window.clearTimeout(timeout)
        resolve()
      }
      const onStateChange = () => {
        if (['installed', 'activated', 'redundant'].includes(worker.state)) finish()
      }

      worker.addEventListener('statechange', onStateChange)
      timeout = window.setTimeout(finish, UPDATE_SETTLE_TIMEOUT_MS)
      onStateChange()
    })
  },

  async checkForUpdate () {
    const registration = state.registration
    if (!registration || state.checkingUpdate) return false
    if (!navigator.onLine) {
      this.setUpdateAvailable(registration)
      return false
    }

    state.checkingUpdate = true
    state.lastUpdateCheck = Date.now()
    state.now = state.lastUpdateCheck
    try {
      await this.waitForInstallingWorker(registration)
      const updatedRegistration = await registration.update()
      state.registration = updatedRegistration || registration
      await this.waitForInstallingWorker(state.registration)
      this.setUpdateAvailable(state.registration)
      return true
    } catch (error) {
      this.setUpdateAvailable(registration)
      return false
    } finally {
      state.checkingUpdate = false
    }
  },

  async install () {
    if (!state.installPrompt) return { outcome: 'unavailable' }
    const prompt = state.installPrompt
    state.installPrompt = null
    state.installable = false
    await prompt.prompt()
    const choice = await prompt.userChoice
    if (choice.outcome !== 'accepted') {
      this.dismissInstall()
    }
    return choice
  },

  dismissInstall () {
    const dismissedUntil = Date.now() + INSTALL_SNOOZE_MS
    state.installDismissedUntil = dismissedUntil
    state.now = Date.now()
    writeTimestamp(INSTALL_SNOOZE_KEY, dismissedUntil)
  },

  applyUpdate () {
    const registration = state.registration
    const waitingWorker = registration && registration.waiting
    if (!waitingWorker || state.updating) return

    state.updating = true
    state.updateError = ''
    state.updateRecoveryAvailable = false
    state.updateDeferred = false
    writeSessionValue(UPDATE_ATTEMPT_KEY, Date.now())
    removeSessionValue(UPDATE_DEFERRED_KEY)

    let handoffTimeout = null

    const reloadOnce = () => {
      if (reloading) return
      reloading = true
      window.clearTimeout(handoffTimeout)
      window.location.reload()
    }

    navigator.serviceWorker.addEventListener('controllerchange', reloadOnce, { once: true })
    waitingWorker.postMessage({ type: 'SKIP_WAITING' })

    handoffTimeout = window.setTimeout(() => {
      if (reloading) return
      state.updating = false
      state.updateError = 'The update could not be applied. Please try again.'
      state.updateRecoveryAvailable = true
    }, UPDATE_SETTLE_TIMEOUT_MS)
  },

  continueWithoutUpdate () {
    state.updateDeferred = true
    state.updating = false
    state.updateError = ''
    state.updateRecoveryAvailable = false
    writeSessionValue(UPDATE_DEFERRED_KEY, true)
  }
}

export default pwaManager
